import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useQuery, useLazyQuery} from '@apollo/client';
import {message, Card, Row, Col, Typography} from 'antd';
import {LoadingOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {DeliveryTask, DeliveryTaskFilter, Driver, DeliveryTaskStatus} from './types/DeliveryTaskType';
import {GET_DELIVERY_TASKS} from './graphql/queries/getDeliveryTasks';
import {GET_AVAILABLE_DRIVERS} from './graphql/queries/getAvailableDrivers';
import {GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID} from './graphql/queries/getAvailableDriversByWarehouseId';
import {GET_TRANSFER_TASK_STATUS, TransferTaskStatusResponse, GetTransferTaskStatusVariables} from './graphql/queries/getTransferTaskStatus';
import DeliveryTaskFilters from './components/DeliveryTaskFilters';
import DeliveryTaskTable from './components/DeliveryTaskTable';
import DeliveryTaskDetailDrawer from './components/DeliveryTaskDetailDrawer';
import AllocateParcelsDrawer from './components/AllocateParcelsDrawer';

import {extractGraphQLMessageKey, translateGraphQLError} from '../../../utils/graphqlUtils';
import {WarehouseContext} from '../../../contexts/WarehouseContext';
import {useContext} from 'react';
import {useCurrentDsp} from '../../../hooks/useCurrentDsp';
import dayjs from 'dayjs';

const { Text } = Typography;

const TOO_MANY_TASK_IDS_KEY = 'errors.business.delivery.task.tooManyTaskIds';

type DeliveryTaskPageMode = 'wms' | 'dsp';

interface DeliveryTaskPageProps {
    mode?: DeliveryTaskPageMode;
}

const DeliveryTaskPage: React.FC<DeliveryTaskPageProps> = ({ mode }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  // 统一使用 WarehouseContext 的所选仓（各 Layout 已从 URL/localStorage 注入），不再回退账号绑定仓
  const warehouseContext = useContext(WarehouseContext);
  const warehouse = warehouseContext?.currentWarehouse ?? null;
  const isDsp = mode === 'dsp';
  const { dspId: currentDspId } = useCurrentDsp();
  
  // 读取URL参数
  const urlWarehouseId = searchParams.get('warehouseId');
  const urlDriverAccountId = searchParams.get('driverAccountId');
  const urlStatus = searchParams.get('status');
  
  // warehouseId 一律取自所选仓（Layout 已从 URL/localStorage 注入 context），不再回退 URL 参数
  const warehouseId = React.useMemo(() => {
    if (warehouse?.id) {
      return typeof warehouse.id === 'string' ? warehouse.id : String(warehouse.id);
    }
    return undefined;
  }, [warehouse?.id]);

  // 获取本地当天日期和近7天日期（避免 toISOString 使用UTC导致跨时区回退到前一天）
  const today = dayjs().format('YYYY-MM-DD');
  const sevenDaysAgo = dayjs().subtract(6, 'day').format('YYYY-MM-DD'); // 近7天（包含今天）

  // 解析URL参数中的状态（支持逗号分隔的多个状态）
  const urlStatusArray = React.useMemo(() => {
    if (!urlStatus) return undefined;
    return urlStatus.split(',').map(s => s.trim()).filter(s => s) as DeliveryTaskStatus[];
  }, [urlStatus]);

  // 初始化时设置筛选条件（默认值，不包含URL参数）
  const [tempFilters, setTempFilters] = useState<DeliveryTaskFilter>(() => {
    const initialFilters: DeliveryTaskFilter = {
      warehouseId: warehouseId ? Number(warehouseId) : undefined,
      taskDateStart: sevenDaysAgo,
      taskDateEnd: today
    };
    return initialFilters;
  });
  
  const [filters, setFilters] = useState<DeliveryTaskFilter>(() => {
    const initialFilters: DeliveryTaskFilter = {
      warehouseId: warehouseId ? Number(warehouseId) : undefined,
      taskDateStart: sevenDaysAgo,
      taskDateEnd: today
    };
    return initialFilters;
  });
  
  // 使用 ref 跟踪URL参数是否已应用
  const urlParamsAppliedRef = useRef(false);
  
  // 当URL参数变化时，重置 ref，允许重新应用URL参数
  React.useEffect(() => {
    urlParamsAppliedRef.current = false;
  }, [urlWarehouseId, urlDriverAccountId, urlStatus]);
  
  // 非DSP角色：等待WmsLayout从URL参数选中仓库后，再设置筛选条件
  // DSP角色：直接设置筛选条件
  React.useEffect(() => {
    // 如果已经应用过URL参数，不再重复应用
    if (urlParamsAppliedRef.current) {
      return;
    }
    
    // 如果没有URL参数，不需要处理
    if (!urlDriverAccountId && !urlStatusArray && !urlWarehouseId) {
      return;
    }
    
    // 非DSP角色且URL中有warehouseId：等待WmsLayout从URL参数选中仓库
    if (!isDsp && urlWarehouseId && mode === 'wms') {
      // WmsLayout会在初始化时从URL参数读取warehouseId并设置仓库
      // 等待仓库切换完成后再设置筛选条件（通过检查 warehouse?.id 是否已更新为 urlWarehouseId）
      if (warehouse?.id === String(urlWarehouseId)) {
        // 仓库已切换，设置筛选条件
        const updatedFilters: Partial<DeliveryTaskFilter> = {
          warehouseId: Number(urlWarehouseId),
          taskDateStart: sevenDaysAgo,
          taskDateEnd: today
        };
        
        if (urlDriverAccountId) {
          updatedFilters.driverIds = [Number(urlDriverAccountId)];
        }
        
        if (urlStatusArray && urlStatusArray.length > 0) {
          updatedFilters.status = urlStatusArray;
        }
        
        setTempFilters(prev => ({ ...prev, ...updatedFilters }));
        setFilters(prev => ({ ...prev, ...updatedFilters }));
        urlParamsAppliedRef.current = true;
      }
    } else {
      // DSP角色或非WMS布局：直接设置筛选条件
      const updatedFilters: Partial<DeliveryTaskFilter> = {
        taskDateStart: sevenDaysAgo,
        taskDateEnd: today
      };
      
      // 非DSP角色且URL中有warehouseId时，设置warehouseId（非WMS布局的情况）
      if (!isDsp && urlWarehouseId) {
        updatedFilters.warehouseId = Number(urlWarehouseId);
      }
      
      if (urlDriverAccountId) {
        updatedFilters.driverIds = [Number(urlDriverAccountId)];
      }
      
      if (urlStatusArray && urlStatusArray.length > 0) {
        updatedFilters.status = urlStatusArray;
      }
      
      setTempFilters(prev => ({ ...prev, ...updatedFilters }));
      setFilters(prev => ({ ...prev, ...updatedFilters }));
      urlParamsAppliedRef.current = true;
    }
  }, [urlWarehouseId, urlDriverAccountId, urlStatusArray, isDsp, mode, warehouse?.id, sevenDaysAgo, today]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskStatus, setSelectedTaskStatus] = useState<string | undefined>(undefined);
  const [currentDriverId, setCurrentDriverId] = useState<number | null>(null);
  const [selectedTaskDspId, setSelectedTaskDspId] = useState<number | undefined>(undefined);
  const [selectedTaskAreaId, setSelectedTaskAreaId] = useState<number | undefined>(undefined);

  // ✅ 转单任务轮询相关状态（参考 RoutesPage）
  const [isPollingTransfer, setIsPollingTransfer] = useState(false);
  const [transferProgress, setTransferProgress] = useState<{
    taskId: number;
    status: string;
    message: string;
  } | undefined>(undefined);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 获取司机列表
  // 策略：
  // 1. 如果有 warehouseId：使用 getAvailableDriversByWarehouseId（不传 dspId，返回整个仓库的司机）
  //    由 DeliveryTaskFilters 和 DeliveryTaskTable 根据各自的 dspId 进行过滤
  // 2. 如果是 DSP 经理且没有 warehouseId：使用 getAvailableDrivers（后端自动从角色获取 dspId）
  const hasWarehouseId = !!warehouseId;
  
  // 调试日志：检查 warehouseId 和查询状态
  React.useEffect(() => {
    console.log('🔍 [DeliveryTaskPage] Warehouse 状态:', {
      warehouse,
      warehouseId,
      hasWarehouseId,
      warehouseContext: warehouseContext?.currentWarehouse,
      skipQuery: !hasWarehouseId
    });
  }, [warehouse, warehouseId, hasWarehouseId, warehouseContext]);

  const { data: driversDataByWarehouse, loading: driversLoadingByWarehouse, error: driversErrorByWarehouse } = useQuery(GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID, {
    variables: { 
      warehouseId: warehouseId ? Number(warehouseId) : undefined!,
      dspId: null  // 不传 dspId，获取整个仓库的司机
    },
    skip: !hasWarehouseId, // 没有 warehouseId 时跳过
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
  
  // 调试日志：检查查询是否被跳过
  React.useEffect(() => {
    if (!hasWarehouseId) {
      console.warn('⚠️ [DeliveryTaskPage] 司机查询被跳过：没有 warehouseId', {
        warehouseId,
        warehouse,
        warehouseContext: warehouseContext?.currentWarehouse
      });
    } else {
      console.log('✅ [DeliveryTaskPage] 司机查询已执行:', {
        warehouseId,
        loading: driversLoadingByWarehouse,
        error: driversErrorByWarehouse,
        driversCount: driversDataByWarehouse?.getAvailableDriversByWarehouseId?.length || 0
      });
    }
  }, [warehouseId, hasWarehouseId, driversLoadingByWarehouse, driversErrorByWarehouse, driversDataByWarehouse]);

  // DSP 经理如果没有 warehouseId，使用原来的 getAvailableDrivers（后端会自动从角色获取 dspId）
  const { data: driversDataByDsp, loading: driversLoadingByDsp } = useQuery(GET_AVAILABLE_DRIVERS, {
    skip: !isDsp || hasWarehouseId // 只有 DSP 经理且没有 warehouseId 时才使用
  });

  // 根据情况选择司机数据源
  const drivers: Driver[] = (isDsp && !hasWarehouseId)
    ? (driversDataByDsp?.getAvailableDrivers || [])
    : (driversDataByWarehouse?.getAvailableDriversByWarehouseId || []);
  
  const driversLoading = (isDsp && !hasWarehouseId)
    ? driversLoadingByDsp
    : driversLoadingByWarehouse;

  // 司机实名验证：全量开启（已移除灰度控制，始终启用）
  const isDriverVerificationEnabled = true;
  
  const [allocateParcelsVisible, setAllocateParcelsVisible] = useState(false);

  // 任务页筛选：保留所有司机（包括离职司机），用于筛选查看
  // 注意：这里不过滤离职司机，因为用户需要能够筛选查看离职司机的任务
  const activeDrivers = drivers;

  // ✅ 查询转单任务状态（用于轮询）
  const [getTransferTaskStatus] = useLazyQuery<TransferTaskStatusResponse, GetTransferTaskStatusVariables>(
    GET_TRANSFER_TASK_STATUS,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      onError: (error) => {
        if (error.networkError) {
          console.warn('GetTransferTaskStatus: Network error during polling:', error.networkError);
        } else {
          console.error('GetTransferTaskStatus failed:', error.message);
        }
      }
    }
  );

  // 当仓库ID变化时，重新刷新页面（参考 ParcelsPage 实现）
  // 注意：如果是从URL参数触发的仓库变化，不重置筛选条件（保留URL参数中的筛选条件）
  useEffect(() => {
    // 如果是从URL参数触发的仓库变化，跳过重置
    // 检查条件：有 urlWarehouseId 或 urlDriverAccountId（表示从URL跳转来的）
    if (urlWarehouseId || urlDriverAccountId) {
      return;
    }
    
    if (warehouseId) {
      // 获取当前日期（在 effect 内部计算，确保每次都是最新的）
      const currentToday = dayjs().format('YYYY-MM-DD');
      
      // 重置过滤器：更新 warehouseId 并重置日期为当天
      const currentWarehouseId = Number(warehouseId);
      const resetFilters = {
        warehouseId: currentWarehouseId,
        taskDateStart: currentToday,
        taskDateEnd: currentToday,
        // 清除其他筛选条件
        areaId: undefined,
        status: undefined,
        dspId: undefined,
        driverIds: undefined,
        publishDate: undefined
      };
      
      setTempFilters(resetFilters);
      setFilters(resetFilters);
      
      // 重置分页到第一页
      setCurrentPage(1);
      
      // 关闭详情抽屉和转单抽屉
      setDetailVisible(false);
      setAllocateParcelsVisible(false);
      setSelectedTaskId(null);
      
      // 停止转单任务轮询（如果有）
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsPollingTransfer(false);
      }
    }
  }, [warehouse?.id, warehouseId, urlWarehouseId, urlDriverAccountId]); // 添加 urlDriverAccountId 到依赖项

  // 获取任务列表
  // 构建查询变量
  const queryVariables = React.useMemo(() => {
    return {
      filter: filters,
      pageRequest: {
        page: currentPage,
        size: pageSize
      }
    };
  }, [filters, currentPage, pageSize]);

  const { data, loading, error, refetch } = useQuery(GET_DELIVERY_TASKS, {
    variables: queryVariables,
    fetchPolicy: 'network-only'  // ✅ 始终从网络获取最新数据，不使用缓存
  });

  const deliveryTasks: DeliveryTask[] = data?.getDeliveryTasks?.content || [];
  const totalElements = data?.getDeliveryTasks?.totalElements || 0;
  const totalPages = data?.getDeliveryTasks?.totalPages || 0;

  // 处理过滤条件变化
  const handleFilterChange = (newFilter: Partial<DeliveryTaskFilter>) => {
    setTempFilters({ ...tempFilters, ...newFilter });
  };

  // 应用过滤条件
  const handleFilter = () => {
    setFilters(tempFilters);
    setCurrentPage(1);
  };

  // 重置过滤条件
  const handleReset = () => {
    const resetWarehouseId = warehouseId ? Number(warehouseId) : undefined;
    const resetFilters = {
      warehouseId: resetWarehouseId,
      taskDateStart: today,
      taskDateEnd: today
    };
    setTempFilters(resetFilters);
    setFilters(resetFilters);
    setCurrentPage(1);
  };

  // ✅ 开始轮询转单任务状态（参考 RoutesPage）
  const startPollingTransferStatus = useCallback(async (transferTaskId: number) => {
    console.log(`🔄 Starting to poll transfer task status for task ${transferTaskId}`);
    
    // 清理任何现有的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setIsPollingTransfer(true);
    setTransferProgress({
      taskId: transferTaskId,
      status: 'PROCESSING',
      message: t('delivery.allocateParcels.message.transferProcessing')
    });
    
    // 开始轮询（每10秒查询一次）
    pollingIntervalRef.current = setInterval(async () => {
      try {
        console.log(`📊 Polling transfer task ${transferTaskId} status...`);
        const statusResult = await getTransferTaskStatus({
          variables: { taskId: transferTaskId }
        });

        const statusData = statusResult.data?.getTransferTaskStatus;
        if (!statusData) {
          console.warn(`⚠️ No status data received for transfer task ${transferTaskId}`);
          return;
        }

        console.log(`📊 Transfer task ${transferTaskId} status:`, statusData);

        // 更新任务状态
        setTransferProgress({
          taskId: transferTaskId,
          status: statusData.status,
          message: statusData.message
        });

        // 如果任务完成或失败，停止轮询
        if (statusData.status === 'COMPLETED' || statusData.status === 'FAILED' || statusData.status === 'PARTIAL_SUCCESS') {
          console.log(`✅ Transfer task ${transferTaskId} finished with status ${statusData.status}`);
          
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPollingTransfer(false);
        } else {
          console.log(`🔄 Transfer task ${transferTaskId} still running (${statusData.status}), continuing to poll...`);
        }
        
      } catch (statusError: any) {
        console.error(`❌ Error checking transfer task ${transferTaskId} status:`, statusError);
        // 网络错误时停止轮询
        if (statusError.networkError) {
          console.warn('🛑 Network error in polling, stopping to prevent spam');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPollingTransfer(false);
          setTransferProgress({
            taskId: transferTaskId,
            status: 'FAILED',
            message: t('errors.system.networkError')
          });
        }
      }
    }, 5000); // 每5秒查询一次
  }, [getTransferTaskStatus, t]);

  // ✅ 组件卸载时清理轮询定时器（参考 RoutesPage）
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        console.log('🧹 Cleaning up transfer polling on component unmount');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // ✅ 监听转单任务完成状态
  useEffect(() => {
    if (transferProgress?.status === 'COMPLETED') {
      message.success({
        content: t('delivery.allocateParcels.message.transferSuccess'),
        duration: 3
      });
      // 刷新任务列表
      setTimeout(() => {
        refetch();
      }, 500);
    } else if (transferProgress?.status === 'PARTIAL_SUCCESS') {
      // ✅ 部分成功：显示警告信息，但仍然刷新列表（因为有成功的任务）
      message.warning({
        content: transferProgress.message || t('delivery.allocateParcels.message.transferPartialSuccess'),
        duration: 6,
        style: {
          marginTop: '20vh',
        }
      });
      // 部分成功也刷新列表，显示成功创建的任务
      setTimeout(() => {
        refetch();
      }, 500);
    } else if (transferProgress?.status === 'FAILED') {
      message.error({
        content: transferProgress.message || t('delivery.allocateParcels.message.transferFailed'),
        duration: 6,
        style: {
          marginTop: '20vh',
        }
      });
    }
  }, [transferProgress?.status, refetch, t]);

  // 查询失败提示放 useEffect：渲染期弹窗会随每次重渲染重复弹出
  useEffect(() => {
    if (!error) return;
    // 仅「任务ID 条数超限」透出后端的具体上限：该 messageKey 三语齐备，且 details 带 max，
    // 用户据此知道该删到多少条。其余错误仍走通用文案
    if (extractGraphQLMessageKey(error) === TOO_MANY_TASK_IDS_KEY) {
      // 只取词条用到的 max：传整个 details 会让 i18next 的 t() 重载解析到 returnDetails 分支
      const details = (error as any)?.graphQLErrors?.[0]?.extensions?.details as
          | { max?: number }
          | undefined;
      message.error(t(TOO_MANY_TASK_IDS_KEY, { max: details?.max }));
    } else {
      message.error(translateGraphQLError(error, 'delivery.tasks.message.getTaskListFailed'));
    }
  }, [error, t]);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  const handleAllocateParcels = (taskId: string) => {
    // ✅ 检查是否正在轮询转单任务，如果是则不允许打开转单抽屉
    if (isPollingTransfer) {
      message.warning({
        content: t('delivery.allocateParcels.message.transferInProgressBlock'),
        duration: 3
      });
      return;
    }
    
    // 递归查找任务记录（包括子行）
    const findTask = (tasks: DeliveryTask[]): DeliveryTask | undefined => {
      for (const task of tasks) {
        if (task.id == taskId) {
          return task;
        }
        if (task.children && task.children.length > 0) {
          const found = findTask(task.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    
    const selectedTask = findTask(deliveryTasks);
    
    // 判断任务状态是否可以转单（COMPLETED 不可转单）
    if (selectedTask && selectedTask.status === 'COMPLETED') {
      message.warning(t('delivery.allocateParcels.message.completedTaskCannotTransfer'));
      return;
    }
    
    setSelectedTaskId(taskId);
    setSelectedTaskStatus(selectedTask?.status);
    setCurrentDriverId(selectedTask?.driverAccountId || null);
    setSelectedTaskDspId(selectedTask?.dspId);
    setSelectedTaskAreaId(selectedTask?.areaId);
    setAllocateParcelsVisible(true);
  };

  const handleViewDetail = (taskId: string) => {
    setSelectedTaskId(taskId);
    setDetailVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailVisible(false);
    setSelectedTaskId(null);
  };

  return (
      <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
        {/* ✅ 转单任务进度卡片（参考 RoutesPage 1755行） */}
        {isPollingTransfer && (
          <Card 
            style={{ 
              margin: '0 0 16px 0', 
              background: '#f6ffed', 
              borderColor: '#b7eb8f' 
            }}
          >
            <Row align="middle" gutter={16}>
              <Col>
                <LoadingOutlined style={{ fontSize: 18, color: '#52c41a' }} />
              </Col>
              <Col flex="auto">
                <Text strong style={{ color: '#52c41a' }}>
                  {transferProgress?.message || t('delivery.allocateParcels.message.transferProcessing')}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('delivery.allocateParcels.message.transferProcessingHint')}
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        <DeliveryTaskFilters
          filters={tempFilters}
          onFilterChange={handleFilterChange}
          onReset={handleReset}
          onApplyFilter={handleFilter}
          drivers={activeDrivers}
          mode={mode}
        />

        <DeliveryTaskTable
          data={deliveryTasks}
          loading={loading}
          currentPage={currentPage}
          pageSize={pageSize}
          totalElements={totalElements}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onViewDetail={handleViewDetail}
          onAllocateParcels={handleAllocateParcels}
          activeDrivers={activeDrivers}
          onDriverChange={refetch}
          filters={filters}
          isDriverVerificationEnabled={isDriverVerificationEnabled}
        />
          {/* Detail Drawer */}
          <DeliveryTaskDetailDrawer
              visible={detailVisible}
              onClose={() => setDetailVisible(false)}
              taskId={selectedTaskId}
              mode={mode}
              warehouseId={warehouse?.id ? Number(warehouse.id) : undefined}
          />

          {/* ✅ 修改 AllocateParcelsDrawer 的 onSuccess 回调 */}
          <AllocateParcelsDrawer
              visible={allocateParcelsVisible}
              onClose={() => setAllocateParcelsVisible(false)}
              onSuccess={(transferTaskId) => {
                if (transferTaskId) {
                  startPollingTransferStatus(transferTaskId);
                }
                refetch();
              }}
              taskId={selectedTaskId || undefined}
              currentDriverId={currentDriverId || undefined}
              taskDspId={selectedTaskDspId}
              isDriverVerificationEnabled={isDriverVerificationEnabled}
              mode={mode}
              />
      </div>
  );
};

export default DeliveryTaskPage;